import { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import GlobalContext from '../../../../../../context/global-context'

// hooks
import { useUpdateDataAdministracion, useAdministracionTabs, useSummaryMovementsOverrides } from '../../../../../../pages/useAdministracion.hooks'
import { useInfiniteMovements } from '../../../../../../hooks/useInfiniteMovements'
import { useInfiniteScroll } from '../../../../../../hooks/useInfiniteScroll'
import { useQueryClient } from '@tanstack/react-query'

// MUI
import { Backdrop } from '@mui/material'
import LoaderAdm from '../../../../../../assets/svgIcons/LoaderAdm.json'
import Lottie from 'lottie-react'

// components
import { Text } from '../../../../ui/index'
import TableEgresos from './TableEgresos'
// import TableEgresosPendientes from './TableEgresosPendientes'
import Select from '../../../../ui/Select'
import { currentMonth, currentYear, monthsOptions as months, getYearsOptionsFromFirstLast, getLatestMovementMonth } from '../../../../../utils/constant'
import CustomButton from '../../../../ui/CustomButton'
import SearchFilter from '../../../../ui/SearchFilter'
import { useGetAccountsTypes, useGetDocumentsTypes } from '../../../../../../queries/businessQueries'
import AdministrationDashboard from '../dashboard'
import CustomPagination from '../../../../table/CustomPagination'
import ExportToXlsxModal from '../../../../modals/ExportToXlsxModal'
import businessApi from '../../../../../../api/business.api'
import { QueryKeys } from '../../../../../../queries/querykeys'
import { CENTRO_COSTO_FILTER_OPTIONS } from '../centroCostoFilter'

const PAGINATION_LIMIT = 30
const ListIngresos = () => {
  const { currentBusiness, firstLast } = useContext(GlobalContext)
  const businessId = currentBusiness?.id || 0
  const setUpdateData = useUpdateDataAdministracion(s => s.setUpdateData)
  const { activeTab } = useAdministracionTabs()
  const overrides = useSummaryMovementsOverrides(state => state.overrides)
  const queryClient = useQueryClient()
  const [visibleData, setVisibleData] = useState([])
  const [latestMovementMonth, setLatestMovementMonth] = useState(null)

  // Estados de filtros
  const [monthFilter, setMonthFilter] = useState(0)
  const [yearFilter, setYearFilter] = useState(currentYear)
  const [documentFilter, setDocumentFilter] = useState('')
  const [accountFilter, setAccountFilter] = useState('')
  const [centroCostoFilter, setCentroCostoFilter] = useState('')
  const [highlightedRows, setHighlightedRows] = useState({})
  const [selectedFilter, setSelectedFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [folioFilter, setFolioFilter] = useState('')
  const [debouncedFolioFilter, setDebouncedFolioFilter] = useState('')
  // const [showPendingTable, setShowPendingTable] = useState(false)
  const [order, setOrder] = useState({
    col: '',
    field: '',
    direction: 'asc'
  })
  const [showModal, setShowModal] = useState({
    show: false,
    title: '',
    body: null
  })
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const yearOptions = useMemo(
    () => getYearsOptionsFromFirstLast(firstLast.data, currentBusiness?.createdAt),
    [firstLast.data, currentBusiness?.createdAt]
  )

  useEffect(() => {
    if (!firstLast.isLoading && !yearOptions.some(option => option.value === Number(yearFilter))) {
      setYearFilter(currentYear)
    }
  }, [firstLast.isLoading, yearFilter, yearOptions])

  const backendFilters = useMemo(() => {
    const filters = {}
    if (documentFilter) filters.code = documentFilter
    if (debouncedSearchQuery) filters.searchQuery = debouncedSearchQuery
    if (selectedFilter && selectedFilter !== 'destacados') filters.documentStatus = selectedFilter
    if (debouncedFolioFilter) filters.folio = debouncedFolioFilter
    if (accountFilter) filters.nombreCuenta = accountFilter
    if (centroCostoFilter) filters.centroCosto = centroCostoFilter
    return filters
  }, [documentFilter, debouncedSearchQuery, selectedFilter, debouncedFolioFilter, accountFilter, centroCostoFilter])

  const {
    data: infiniteData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch
  } = useInfiniteMovements({
    businessId,
    year: yearFilter,
    month: monthFilter,
    linkage: true,
    limit: PAGINATION_LIMIT,
    filters: {
      type: 'egreso',
      ...backendFilters
    },
    aprobadoMin: -5
  })

  const paginationDetails = useMemo(() => {
    if (!infiniteData?.pages) {
      return {
        count: 0,
        rowsPerPage: PAGINATION_LIMIT,
        page: 0
      }
    }
    const count = infiniteData.pages.reduce((acc, page) => acc + page.count, 0)
    return {
      count,
      rowsPerPage: count,
      page: 0
    }
  }, [infiniteData?.pages])

  const { data: documentsTypes } = useGetDocumentsTypes(
    businessId,
    yearFilter,
    monthFilter,
    'egreso'
  )
  const { data: accountsTypes = [] } = useGetAccountsTypes(
    businessId,
    yearFilter,
    monthFilter,
    'egreso'
  )

  const { lastElementRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  })

  const allData = useMemo(() => {
    const base = infiniteData?.pages?.flatMap(page => page.data) || []
    if (!overrides || Object.keys(overrides).length === 0) return base
    const hasOverride = new Map(Object.entries(overrides).map(([k, v]) => [Number(k), v]))
    return base.map(row => hasOverride.get(Number(row?.id)) ?? row)
  }, [infiniteData, overrides])

  useEffect(() => {
    setVisibleData([])
  }, [activeTab])

  useEffect(() => {
    setVisibleData(allData)
  }, [allData])

  useEffect(() => {
    setLatestMovementMonth(null)
  }, [businessId, yearFilter])

  useEffect(() => {
    if (Number(monthFilter) !== 0 || !infiniteData?.pages) return
    const latestMonth = getLatestMovementMonth(allData, yearFilter)
    setLatestMovementMonth(current => Math.max(current ?? 0, latestMonth))
  }, [allData, infiniteData?.pages, monthFilter, yearFilter])

  const documentTypesFilter = useMemo(() => {
    if (!documentsTypes) return [{ value: '', label: 'Todos los documentos' }]
    return [
      { value: '', label: 'Todos los documentos' },
      ...documentsTypes.map(({ code, name }) => ({
        value: code,
        label: name
      }))
    ]
  }, [documentsTypes])

  // destacados
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('highlightedRows') || '{}')
    setHighlightedRows(saved)
  }, [])

  // Actualizar datos solo cuando sea necesario
  useEffect(() => {
    if (businessId && businessId > 0) {
      setUpdateData(true)
    }
  }, [businessId, setUpdateData])

  useEffect(() => {
    if (activeTab === 'proveedores') {
      setUpdateData(true)
      refetch()
    }
  }, [activeTab, refetch, setUpdateData])

  useEffect(() => {
    if (!activeTab) return
    refetch()
  }, [activeTab, refetch])

  useEffect(() => {
    if (!activeTab) return
    queryClient.removeQueries({ queryKey: [QueryKeys.SUMMARY_MOVEMENTS_FROM_TO], exact: false })
  }, [activeTab, queryClient])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFolioFilter(folioFilter)
    }, 500)

    return () => clearTimeout(timer)
  }, [folioFilter])

  const monthsOptions = useMemo(() => [
    { value: '', label: 'Todos los meses' },
    ...months.map(opt => ({
      ...opt,
      disabled: opt.value > Math.max(
        Number(yearFilter) < currentYear
          ? 12
          : Number(yearFilter) === currentYear
            ? currentMonth
            : 0,
        latestMovementMonth ?? 0
      )
    }))
  ], [latestMovementMonth, yearFilter])

  const filtroDocumento = useMemo(() => documentTypesFilter, [documentTypesFilter])
  const accountFilterOptions = useMemo(
    () => [{ value: '', label: 'Tipo de cuenta' }, ...accountsTypes],
    [accountsTypes]
  )

  useEffect(() => {
    if (accountFilter && !accountFilterOptions.some(option => option.value === accountFilter)) {
      setAccountFilter('')
    }
  }, [accountFilter, accountFilterOptions])

  const filteredData = useMemo(() => {
    if (selectedFilter !== 'destacados') return visibleData

    return visibleData.filter(i => highlightedRows[i.id])
  }, [visibleData, selectedFilter, highlightedRows])

  // Ordenamiento local - solo si es necesario
  const sortedData = useMemo(() => {
    if (!order.field) return filteredData

    return [...filteredData].sort((a, b) => {
      const A = a[order.field]
      const B = b[order.field]
      if (A == null) return 1
      if (B == null) return -1
      return order.direction === 'asc'
        ? A < B ? -1 : A > B ? 1 : 0
        : A > B ? -1 : A < B ? 1 : 0
    })
  }, [filteredData, order.field, order.direction])

  const handleSort = useCallback(({ col, field }) => {
    const asc = order.col === col && order.direction === 'asc'
    setOrder({ col, field, direction: asc ? 'desc' : 'asc' })
  }, [order.col, order.direction])

  const toggleHighlight = useCallback(id => {
    setHighlightedRows(prev => {
      const next = { ...prev, [id]: !prev[id] }
      localStorage.setItem('highlightedRows', JSON.stringify(next))
      return next
    })
  }, [])

  const handleExport = async (email) => {
    setIsExporting(true)
    try {
      await businessApi.exportSummaryMovements({
        organizationId: currentBusiness.organizationId,
        businessId: currentBusiness.id,
        email,
        type: 'egreso',
        month: monthFilter || new Date().getMonth() + 1,
        year: yearFilter,
        linkage: true,
        ...(documentFilter && { code: documentFilter }),
        ...(debouncedSearchQuery && { searchQuery: debouncedSearchQuery }),
        ...(selectedFilter && selectedFilter !== 'destacados' && { documentStatus: selectedFilter })
      })
    } catch (error) {
      console.error('Error al exportar:', error)
      throw error
    } finally {
      setIsExporting(false)
    }
  }

  const options = [
    { label: 'Destacados', value: 'destacados' },
    { label: 'Pagado', value: 'PAGADO' },
    { label: 'Por pagar', value: 'POR PAGAR' },
    { label: 'Parcialmente pagado', value: 'PARCIALMENTE PAGADO' }
  ]

  const Modal = () => {
    const { show, title, body } = showModal
    if (!show) return null
    return (
      <Backdrop sx={{ zIndex: 1600 }} open>
        <div className="py-6 px-12 flex flex-col items-center bg-white">
          <Text variant="h6" className="text-xl mb-4">{title}</Text>
          {body}
          <button
            className="bg-primary text-white rounded-full px-4 py-2 mt-4"
            onClick={() => setShowModal(prev => ({ ...prev, show: false }))}
          >
            Cerrar
          </button>
        </div>
      </Backdrop>
    )
  }

  const icons = useMemo(() => [
    <button
      key='export-xlsx'
      className='w-10 h-10 text-green-700 font-bold cursor-pointer rounded-full shadow bg-white'
      onClick={() => setIsExportModalOpen(true)}
    >
      XLS
    </button>
  ], [])

  return (
    <>
      <AdministrationDashboard year={yearFilter} highlighnedIndex={2} />
      <div className="p-3 hd:p-4 mt-6 min-w-0 bg-white rounded-3xl shadow-md flex flex-col" style={{ height: 'max(24rem, calc(var(--app-viewport-height) - 12rem))' }}>
        <div className='relative'>
          <div className='absolute top-[-35px] right-0 z-20 flex items-center space-x-2'>
            {icons.map((icon) => icon)}
          </div>
        </div>
          <div className="flex flex-wrap mt-3 gap-2 items-center rounded-3xl p-4 mb-4">
            <Select key={`month-${monthFilter}-${yearFilter}`} options={monthsOptions} defaultValue={monthFilter} onChange={e => setMonthFilter(e.target.value)} />
            <Select options={yearOptions} value={yearFilter} onChange={e => setYearFilter(e.target.value)} />
            <Select key={`document-${documentFilter}`} options={filtroDocumento} defaultValue={documentFilter} onChange={e => setDocumentFilter(e.target.value)} />
            <Select value={accountFilter} options={accountFilterOptions} onChange={e => setAccountFilter(e.target.value)} />
            <Select value={centroCostoFilter} options={CENTRO_COSTO_FILTER_OPTIONS} onChange={e => setCentroCostoFilter(e.target.value)} />
            <SearchFilter value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            <SearchFilter
              placeholder='Buscar por folio...'
              value={folioFilter}
              onChange={e => setFolioFilter(e.target.value)}
            />
            <select
              value={selectedFilter}
              onChange={e => setSelectedFilter(e.target.value)}
              className="min-w-[90px] border border-gray-300 hover:border-gray-400 focus:border-gray-400 px-4 py-1 pr-6 rounded-full shadow leading-tight text-sm font-sans"
            >
              <option value="">Todos</option>
              {options.map(opt => (
                <option key={opt.value} value={opt.value} className='font-sans'>
                  {opt.label}
                </option>
              ))}
            </select>
            <CustomButton className="rounded-full" color="primary" title="BUSCAR" onClick={() => refetch()} />
            {/* <label className='flex cursor-pointer items-center gap-2 px-2 text-sm font-medium text-gray-600'>
              <input
                type='checkbox'
                checked={showPendingTable}
                onChange={event => setShowPendingTable(event.target.checked)}
                className='h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary'
              />
              Pendientes Aceptación
            </label> */}
          </div>
        
        {/* {showPendingTable
          ? (
            <div className='flex-1 min-h-0 overflow-auto pb-2'>
              <TableEgresosPendientes onSort={handleSort} order={order} />
            </div>
          )
          : */}{isLoading
          ? <div className="flex items-center justify-center h-full"><Lottie animationData={LoaderAdm} loop style={{ width: 300, height: 300 }} /></div>
          : (
            <div className="flex-1 min-h-0 overflow-auto pb-2">
              <TableEgresos
                rows={sortedData}
                onSort={handleSort}
                order={order}
                getData={refetch}
                highlightedRows={highlightedRows}
                toggleHighlight={toggleHighlight}
              />
              {hasNextPage && (
                <div ref={lastElementRef} className="flex justify-center py-4">
                  {isFetchingNextPage ? (
                    <Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />
                  ) : (
                    <button
                      onClick={() => fetchNextPage()}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Cargar más
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        }
        {/* {!showPendingTable && ( */}
          <div className='mt-4'>
            <CustomPagination
              count={paginationDetails?.count || 0}
              rowsPerPage={paginationDetails?.rowsPerPage || PAGINATION_LIMIT}
              page={paginationDetails?.page || 0}
            />
          </div>
        {/* )} */}
        <Modal />
        <ExportToXlsxModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          onExport={handleExport}
          title='Exportar Egresos'
          description='Se enviará un archivo Excel con los egresos filtrados al email especificado'
          isLoading={isExporting}
        />
      </div>
    </>
  )
}

export default ListIngresos
